<template>
    <div class="group-filter">
        <div v-if="groupLoadError" class="unified-filter-row">
            <span class="filter-content-text">Groups failed to load. This filter is temporarily unavailable.</span>
        </div>

        <template v-else>
            <div v-if="availableGroups.length > 0" class="unified-filter-row group-search-row" @click.stop>
                <e-input
                    v-model="searchTerm"
                    clearable
                    expanded
                    size="is-small"
                    placeholder="Search groups..."
                    aria-label="Search groups"
                    class="tw:w-full"
                />
            </div>

            <div v-if="localSelectedGroupIds && localSelectedGroupIds.length > 0" class="selected-groups-list">
                <div class="selected-items">
                    <div
                        v-for="groupId in localSelectedGroupIds"
                        :key="`selected-group-${groupId}`"
                        class="unified-filter-row"
                    >
                        <span class="filter-content-text">{{ getGroupName(groupId) }}</span>
                        <div style="margin-left: auto;">
                            <button
                                class="remove-filter-button-fixed"
                                :aria-label="`Remove ${getGroupName(groupId)} filter`"
                                @click.stop="removeGroupFromFilter(groupId, filter)"
                            >
                                <e-icon icon="times" size="is-small" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div :key="`available-groups-${(filter.value || []).join('-')}`" class="available-groups-list">
                <div class="available-items available-items-container">
                    <div
                        v-if="filteredAvailableGroups.length === 0"
                        class="unified-filter-row"
                    >
                        <span class="filter-content-text">{{ emptyStateLabel }}</span>
                    </div>
                    <div
                        v-for="(group, index) in filteredAvailableGroups"
                        :key="`available-group-${group.id}-${index}`"
                        class="unified-filter-row clickable-item filter-option-row"
                        :class="{ 'has-border-bottom': index < filteredAvailableGroups.length - 1 }"
                        @click.stop="addGroupToFilter(group.id, filter)"
                    >
                        <e-checkbox
                            :model-value="false"
                            readonly
                            size="is-small"
                            @click.stop.prevent="addGroupToFilter(group.id, filter)"
                        />
                        <span class="filter-content-text">
                            {{ group.name }}
                        </span>
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
export default {
    name: 'GroupFilterItem',

    props: {
        filter: {
            type: Object,
            required: true,
        },
        availableGroups: {
            type: Array,
            required: true,
        },
        groupLoadError: {
            type: Boolean,
            required: false,
            default: false,
        },
        localSelectedGroupIds: {
            type: Array,
            required: true,
        },
        addGroupToFilter: {
            type: Function,
            required: true,
        },
        removeGroupFromFilter: {
            type: Function,
            required: true,
        },
        getGroupName: {
            type: Function,
            required: true,
        },
    },

    data() {
        return {
            searchTerm: '',
        };
    },

    computed: {
        filteredAvailableGroups() {
            const normalizedSearch = this.searchTerm.trim().toLowerCase();
            if (!normalizedSearch) {
                return this.availableGroups;
            }

            return this.availableGroups.filter(group => group.name.toLowerCase().includes(normalizedSearch));
        },

        emptyStateLabel() {
            return this.searchTerm.trim().length > 0 ? 'No groups found' : 'All groups are selected';
        },
    },
};
</script>

<style scoped>
.group-filter {
    width: 100%;
}

.selected-items {
    margin-bottom: 8px;
}

.group-search-row {
    padding-bottom: 8px;
}

.available-items-container {
    background: transparent;
    max-height: 265px;
    overflow-y: auto;
}

.has-border-bottom {
    border-bottom: 1px solid var(--p-surface-border);
}

.filter-option-row:hover,
.filter-option-row:focus-within {
    background-color: var(--surface-hover, var(--p-content-hover-background)) !important;

    .filter-content-text {
        color: var(--p-content-hover-color, var(--text-color)) !important;
    }
}

.clickable-item {
    cursor: pointer;
    transition: background-color 0.15s ease;
}

.filter-option-row.unified-filter-row {
    align-items: center;
}

.clickable-item .filter-content-text {
    display: flex;
    align-items: center;
    line-height: 1.25rem;
    min-height: 1.25rem;
}

.clickable-item :deep(.e-checkbox-wrapper),
.clickable-item :deep(.p-checkbox) {
    display: inline-flex;
    align-items: center;
    flex-shrink: 0;
    line-height: 0;
}

.unified-filter-row {
    display: flex;
    padding: 7.2px 14.4px;
    align-items: center;
    gap: 8px;
    align-self: stretch;
}

.filter-content-text {
    color: var(--text-color);
    font-family: Stolzl;
    font-size: 13.68px;
    font-style: normal;
    font-weight: 400;
    line-height: 150%;
}

.remove-filter-button-fixed {
    border: none !important;
    background: transparent !important;
    color: var(--text-color-secondary) !important;
    padding: 2px !important;
    height: auto !important;
    min-height: auto !important;
    line-height: 1 !important;
    cursor: pointer;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
}
</style>
